import { getMomentTz, TimeService, HelmetDexteel } from "@dexteel/mesf-core";
import { closeWorkOrder } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import type { ModalWorkOrder } from "@/shared/work-orders-modals/models/ModalWorkOrder";
import { DateTimePicker } from "@mui/x-date-pickers";

import { ErrorModal, MESApiService, useToken } from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	TextField,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Snackbar from "@material-ui/core/Snackbar";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import { type SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useWorkOrdersContext } from "../../../pages/work-orders/context/workOrdersContext";
import type { WorkOrder } from "../../../pages/work-orders/models/WorkOrder";
import { Controller, useForm } from "react-hook-form";
import { INITIAL_VALUES_MODAL } from "@/shared/work-orders-modals/models/ModalInitialValue";
import { CraftPicker } from "./picker/craftPicker";
import { useGetAssetPath } from "@/shared/hooks/useGetAssetFullPath";
import { TableWorkOrdesResoures } from "./work-order-resources/table-work-ordes-resoures";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";

const moment = getMomentTz();

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiPaper-root": {
			height: "auto",
		},
		"& .MuiSelect-iconOutlined": {
			display: "none",
		},
		"& .MuiInputAdornment-root": {
			marginTop: "-8px",
		},
		"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: "#3F51B5",
		},
		"& .MuiFormLabel-root.Mui-focused": {
			color: "#3F51B5",
		},
	},
	dateTimePicker: {},
	"@global": {
		"*::-webkit-scrollbar": {
			width: "10px",
			background: "#fff",
		},
		"*::-webkit-scrollbar-thumb": {
			backgroundColor: "	#C8C8C8",
		},
		"*::-webkit-scrollbar-track": {
			borderRadius: "10px",
		},
		"*::-webkit-scrollbar:horizontal": {
			height: "10px",
		},
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	inputEditable: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
		margin: 0,
	},
}));

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	data: ModalWorkOrder | null;
	suffixTitle?: string;
};

export function CloseWorkOrderModalWithAttachments({
	show,
	onHide,
	data,
	suffixTitle,
}: Props) {
	const classes = useStyles();
	const { getUserName } = useToken();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [linkAttachmentError, setLinkAttachmentError] = useState<string>("");
	const [closeSuccess, setCloseSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [updateWhenClose, setUpdateWhenClose] = useState(false);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [openWorkOrdersResources, setOpenWorkOrdersResources] = useState(false);
	const [assetChanged, setAssetChanged] = useState(false);

	const {
		state,
		state: {
			modalWorkOrderSelected: {
				Comments,
				TemplateTitle,
				WorkOrderPlannedHours,
				WorkOrderOpenedBy,
				WorkOrderName,
				Title,
				AssetName,
				DownHours,
				WorkOrderId,
				AssetId,
			},
		},
		actions: {
			setComments,
			setWorkOrderCloseTimestamp,
			setWorkOrderNumberOfWorkers,
			setWorkOrderConsumedHours,
			setTemplateTitle,
			setWorkOrderPlannedHours,
			setWorkOrderOpenedBy,
			setOpenTimestamp,
			setWorkOrderName,
			setTitle,
			setAssetName,
			setWorkOrderId,
			setDownHours,
			setAttachmentsLinked,
			setAssetId,
			setCraftId,
			setTypeCode,
		},
	} = useWorkOrderModalsContext();
	const [isLoadingAssetPath, setIsLoadingAssetPath] = useState<boolean>(true);
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");

	const { GetAssetFullPath } = useGetAssetPath({
		setIsLoadingAssetPath,
		setErrorAssetPath,
		setAssetName,
	});
	const {
		state: { selectedFilesToAdd },
		actions: { setSelectedFilesToAdd, setAttachmentsToAddToWorkOrder },
	} = useWorkOrdersContext();

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<ModalWorkOrder>({ defaultValues: INITIAL_VALUES_MODAL });

	const [userInput, setUserInput] = useState<string>("");
	const api = new MESApiService();

	//functions
	const appendComment = (newComment: string) => {
		const now = moment(new Date()).format("MM/DD/YYYY HH:mm");
		let newValue = "";

		if (Comments === "" || Comments === null || Comments === "null") {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				Comments + "\n" + getUserName() + " - " + now + " - " + newComment;
		}
		if (newComment != "") {
			setUserInput("");
			setComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(userInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setCloseSuccess(false);
	};

	const handleCancel = () => {
		onHide(false);
	};

	const handleWorkOrderResourcesDialog = () => {
		setOpenWorkOrdersResources(true);
	};

	const title = watch("Title");
	const changeTitle = () => {
		setTitle(title);
	};

	const closeTimestamp = watch("CloseTimestamp");
	const changeCloseTimestamp = () => {
		setWorkOrderCloseTimestamp(closeTimestamp);
	};

	const workOrderNumberOfWorkers = watch("WorkOrderNumberOfWorkers");
	const changeWorkOrderNumberOfWorkers = () => {
		setWorkOrderNumberOfWorkers(
			Number.parseInt(workOrderNumberOfWorkers as any),
		);
	};

	const consumedHours = watch("ConsumedHours");
	const changeConsumedHours = () => {
		setWorkOrderConsumedHours(consumedHours);
	};

	const onSubmit = async () => {
		setIsSubmitLoading(true);
		if (consumedHours) {
			const consumedHoursValue = (consumedHours as number)
				.toString()
				.replace(",", ".");
			setWorkOrderConsumedHours(Number.parseFloat(consumedHoursValue));
		}
		const res = await closeWorkOrder(state.modalWorkOrderSelected as WorkOrder);
		if (res.ok) {
			if (selectedFilesToAdd.length >= 1)
				await api
					.uploadFiles(selectedFilesToAdd, "Attachments")
					.then((res) => setAttachmentsToAddToWorkOrder(res.result));
			setIsSubmitLoading(false);
			reset();
			setCloseSuccess(true);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	const changeComments = (value: string) => {
		setComments(value);
	};

	const formatDescription = (description: string): string => {
		if (description?.includes("/") && description.split("/").length > 3) {
			const parts = description.split("/");
			let formattedDescription = "";

			for (let i = 0; i < parts.length; i++) {
				if (i < parts.length - 2) {
					formattedDescription += ".../";
				} else if (i === parts.length - 2) {
					if (parts[i].length > 6) {
						formattedDescription += parts[i].substring(0, 6) + ".../";
					} else {
						formattedDescription += parts[i] + "/";
					}
				} else {
					formattedDescription += parts[i];
					if (i < parts.length - 1) {
						formattedDescription += "/";
					}
				}
			}

			return formattedDescription;
		} else {
			return description;
		}
	};

	const description = useMemo(
		() => formatDescription(AssetName as string),
		[AssetName],
	);

	useEffect(() => {
		if (data != null && show) {
			setComments(data?.Comments);
			setTemplateTitle(data?.TemplateTitle);
			setWorkOrderPlannedHours(data?.WorkOrderPlannedHours);
			setWorkOrderNumberOfWorkers(data?.WorkOrderNumberOfWorkers);
			setWorkOrderOpenedBy(data?.WorkOrderOpenedBy);
			setOpenTimestamp(data?.OpenTimestamp);
			setWorkOrderName(data?.WorkOrderName);
			setTitle(data?.Title);
			GetAssetFullPath((data?.AssetId as number)?.toString());
			setAssetId(data?.AssetId);
			setWorkOrderId(data?.WorkOrderId);
			setDownHours(data?.DownHours);
			setWorkOrderConsumedHours(data?.ConsumedHours);
			setWorkOrderCloseTimestamp(data?.CloseTimestamp);
			setAttachmentsLinked(data?.AttachmentsLinked);
			setCraftId(data?.CraftId);
			setTypeCode(data?.TypeCode);
			setSelectedFilesToAdd([]);
			setValue("WorkOrderNumberOfWorkers", data?.WorkOrderNumberOfWorkers);
			setValue("ConsumedHours", data?.ConsumedHours);
			setValue("CloseTimestamp", moment().toDate());
			setValue("CraftId", data?.CraftId);
			setValue("Title", data?.Title);

			setUserInput("");
			setIsSubmitLoading(false);
		} else {
			if (updateWhenClose) {
				onHide(true);
			} else {
				onHide(false);
			}
			setUpdateWhenClose(false);
		}
	}, [show]);

	useEffect(() => {
		changeTitle();
	}, [title]);

	useEffect(() => {
		changeWorkOrderNumberOfWorkers();
	}, [workOrderNumberOfWorkers]);

	useEffect(() => {
		changeConsumedHours();
	}, [consumedHours]);

	useEffect(() => {
		changeCloseTimestamp();
	}, [closeTimestamp]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Close Work Order${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
				className={classes.root}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid container style={{ padding: "15px 0 0" }}>
						<Grid item md={12} xs={12}>
							<DialogTitle
								style={{
									padding: "0px 40px 10px 24px",
									marginRight: 20,
									width: "100%",
								}}
							>
								<Grid
									item
									md={12}
									xs={12}
									style={{ fontSize: "1.7em", fontWeight: "bold" }}
								>
									CLOSE: {WorkOrderName}
								</Grid>
								<Grid item md={12} xs={12}>
									{TemplateTitle && `${data?.TemplateTitle}`}
								</Grid>
							</DialogTitle>
						</Grid>
						<Grid container item>
							<DialogContent
								dividers
								style={{
									height: "auto",
									paddingTop: "0px",
									justifyContent: "space-between !important",
								}}
							>
								<Grid
									container
									style={{ justifyContent: "space-between !important" }}
								>
									<Grid
										item
										md={12}
										xs={12}
										style={{ padding: "0", marginTop: 10 }}
									>
										<TextField
											label="Title"
											variant="outlined"
											disabled
											value={Title}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid
										container
										item
										md={12}
										xs={12}
										style={{ margin: "-5px 0 0 0", padding: 0 }}
										spacing={2}
									>
										<Grid item md={6} xs={12} style={{ paddingLeft: 0 }}>
											<TextField
												label="Asset"
												variant="outlined"
												disabled
												value={description || ""}
												fullWidth
												margin="dense"
												autoComplete="off"
											/>
										</Grid>
										<Grid item md={6} xs={12} style={{ paddingRight: 0 }}>
											<TextField
												label="Opened by"
												variant="outlined"
												disabled
												value={WorkOrderOpenedBy}
												fullWidth
												margin="dense"
												autoComplete="off"
											/>
										</Grid>
									</Grid>
									<Grid
										container
										item
										md={12}
										xs={12}
										style={{ margin: "-5px 0 0 0", padding: 0 }}
										spacing={2}
									>
										<Grid item md={3} xs={6} style={{ paddingLeft: 0 }}>
											<TextField
												label="Planned Hours:"
												variant="outlined"
												disabled
												value={WorkOrderPlannedHours}
												fullWidth
												margin="dense"
												autoComplete="off"
											/>
										</Grid>
										<Grid item md={3} xs={6}>
											<TextField
												label="DownHours"
												variant="outlined"
												disabled
												value={DownHours}
												fullWidth
												margin="dense"
												autoComplete="off"
											/>
										</Grid>
										<Grid item md={3} xs={6}>
											<Controller
												name="CraftId"
												control={control}
												render={({ field: { onChange, value } }) => (
													<CraftPicker
														value={value as number}
														onChange={onChange}
														disabled={true}
													/>
												)}
											/>
										</Grid>
										<Grid item md={3} xs={6} style={{ paddingRight: 0 }}>
											<TextField
												label="Opened"
												variant="outlined"
												disabled
												value={moment(data?.OpenTimestamp).format(
													"MM/DD/YYYY HH:mm A",
												)}
												fullWidth
												margin="dense"
												autoComplete="off"
											/>
										</Grid>
									</Grid>

									<Grid
										item
										md={4}
										xs={12}
										style={{ paddingLeft: 10, marginTop: 10 }}
									></Grid>
									<Grid container spacing={2} style={{ height: "auto" }}>
										<Grid
											item
											md={4}
											xs={6}
											style={{ padding: "0 10px 0 10px" }}
										>
											<Grid item xs={12} md={12}>
												<Controller
													name="CloseTimestamp"
													control={control}
													rules={{
														required: true,
														validate: (value) => {
															if (value === null || value === undefined) {
																return "Date is required";
															}
															const currentDate = new Date();
															return (
																value <= currentDate ||
																"Date cannot be in the future"
															);
														},
													}}
													render={({ field }) => (
														<DateTimePicker
															label="CloseTimestamp"
															timezone={TimeService.getInstance().getServerTimeZone()}
															format="MM/DD/YYYY HH:mm"
															timeSteps={{ minutes: 1 }}
															value={moment(field.value) as any}
															onChange={field.onChange}
															inputRef={field.ref}
															sx={{
																"& .MuiFormLabel-root": {
																	marginTop: "5px !important",
																},
																"& .MuiButtonBase-root": {
																	marginTop: "8px !important",
																},
															}}
															slotProps={{
																textField: {
																	fullWidth: true,
																	variant: "outlined",
																	error: !!errors.CloseTimestamp,
																	className: `${classes.inputEditable}`,
																	InputProps: {
																		style: {
																			height: "40px",
																			marginTop: 8.5,
																		},
																	},
																	inputProps: {
																		style: {
																			paddingTop: "16px",
																		},
																	},
																},
															}}
														/>
													)}
												/>
												{errors.CloseTimestamp && (
													<p className={classes.errorLabel}>
														{typeof errors.CloseTimestamp.message === "string"
															? errors.CloseTimestamp.message
															: "Close date cannot be in the future"}
													</p>
												)}
											</Grid>
										</Grid>
										<Grid item md={4} xs={6} style={{ padding: "0 10px 0 0" }}>
											<Controller
												name="ConsumedHours"
												control={control}
												rules={{
													pattern: {
														value: /^(?:0|[1-9]\d*)$/,
														message: "Enter a valid number",
													},
												}}
												render={({ field: { onChange, value } }) => (
													<TextField
														label="Consumed Hours"
														variant="outlined"
														error={!!errors.ConsumedHours}
														fullWidth
														margin="dense"
														autoComplete="off"
														value={value}
														onChange={(e) => onChange(e)}
														className={classes.inputEditable}
													/>
												)}
											/>
										</Grid>
										<Grid item md={4} xs={6} style={{ padding: "0 10px 0 0" }}>
											<Controller
												name="WorkOrderNumberOfWorkers"
												control={control}
												rules={{
													pattern: {
														value: /^-?\d*(.\d+)?$/,
														message: "Enter a valid number",
													},
												}}
												render={({ field: { onChange, value } }) => (
													<TextField
														label="Workers"
														variant="outlined"
														error={!!errors.WorkOrderNumberOfWorkers}
														fullWidth
														margin="dense"
														autoComplete="off"
														value={value}
														onChange={(e) => onChange(e)}
														className={classes.inputEditable}
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>
								<Grid container className={classes.root}>
									<Grid
										item
										md={12}
										xs={12}
										style={{
											marginBottom: "10px",
											marginTop: "22px",
											padding: 0,
										}}
									>
										<Card
											variant="outlined"
											style={{ backgroundColor: "#E9F2F8" }}
											className={classes.root}
										>
											<CardContent>
												<Typography className={classes.messageTitle}>
													Comments:
												</Typography>
												<SimpleTextAreaControl
													value={Comments || ""}
													onChange={changeComments}
													disabled
													showColor={false}
													styleText={{
														height: "100px",
														border: "none",
														backgroundColor: "#E9F2F8",
														fontSize: 16,
													}}
												/>
											</CardContent>
										</Card>
									</Grid>
									<Grid
										container
										spacing={2}
										justifyContent="center"
										alignItems="center"
										style={{ margin: "0 -8px" }}
									>
										<Grid item md={9} xs={12} style={{ padding: "0 8px" }}>
											<TextField
												variant="outlined"
												margin="dense"
												multiline
												maxRows={3}
												value={userInput}
												onChange={(e) => setUserInput(e.target.value)}
												label="Comment"
												fullWidth
												onKeyDown={onKeyDown}
												autoComplete="off"
											/>
										</Grid>
										<Grid item md={3} xs={12}>
											<Button
												fullWidth
												variant="contained"
												color="primary"
												onClick={addNewComment}
												style={{
													height: "40px",
													marginTop: 2,
													padding: "0 8px",
												}}
											>
												Add comment
											</Button>
										</Grid>
									</Grid>
								</Grid>
							</DialogContent>
						</Grid>
						<Grid
							item
							md={12}
							xs={12}
							justifyContent="flex-end"
							style={{ marginTop: "4px", paddingBottom: "5px" }}
						>
							<DialogActions style={{ padding: "0px 24px" }}>
								<Grid container>
									<Grid
										item
										md={4}
										xs={4}
										spacing={2}
										style={{ display: "flex", alignItems: "center" }}
										title={
											assetChanged
												? "You need to save to be able to manage resources"
												: ""
										}
									>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											disabled={assetChanged}
											onClick={handleWorkOrderResourcesDialog}
											title={
												assetChanged
													? "You need to save to be able to manage resources"
													: ""
											}
										>
											MANAGE RESOURCES
										</Button>
									</Grid>
									<Grid
										item
										md={8}
										xs={8}
										container
										justifyContent="flex-end"
										spacing={2}
										style={{ margin: 0 }}
									>
										<Grid item md={4} xs={4}>
											<Button
												fullWidth
												variant="contained"
												color="default"
												onClick={handleCancel}
											>
												CANCEL
											</Button>
										</Grid>
										<Grid item md={4} xs={4} style={{ paddingRight: 0 }}>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={isSubmitLoading}
												variant="contained"
												color="secondary"
												type="submit"
											>
												close order
											</Button>
										</Grid>
									</Grid>
								</Grid>
							</DialogActions>
						</Grid>
					</Grid>
				</form>
				<TableWorkOrdesResoures
					show={openWorkOrdersResources}
					onHide={(shouldUpdate: boolean) => {
						setOpenWorkOrdersResources(false);
					}}
					AssetId={AssetId}
					WorkOrderId={WorkOrderId}
					WorkOrderName={data?.WorkOrderName as string}
					Title={data?.Title as string}
					AssetName={(data?.AssetName as string)?.replace(/.*\/(.+)$/, "$1")}
					TypeCode={data?.TypeCode as string}
					CraftId={data?.CraftId as number}
					OpenTimestamp={data?.OpenTimestamp as Date}
					CloseTimestamp={data?.CloseTimestamp as Date}
				/>
			</Dialog>
			<Snackbar
				open={closeSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="warning" onClose={handleClose}>
					Work order closed
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={linkAttachmentError}
				onHide={() => setLinkAttachmentError("")}
			/>
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
}
